import { useRef, useCallback, useEffect } from 'react';
import useFokusMode from '../shared/useFokusMode';

/**
 * useFokusModeRefs Hook
 * 
 * Manages FOKUS mode refs and callbacks for all view types.
 * Creates stable callbacks that can be connected to custom view settings
 * after the customViewSettings hook is created.
 */
const useFokusModeRefs = ({
  tasks,
  statuses,
  nestedSprints,
  kanbanSelectedIds,
  tableSelectedIds,
  ganttSelectedIds,
  todoSelectedIds,
  neuralSelectedIds,
}) => {
  // Refs to hold FOKUS change handlers (connected after customViewSettings is created)
  const kanbanFokusChangeRef = useRef(null);
  const tableFokusChangeRef = useRef(null);
  const ganttFokusChangeRef = useRef(null);
  const todoFokusChangeRef = useRef(null);
  const neuralFokusChangeRef = useRef(null);
  
  // Create stable callbacks that use the refs
  const handleKanbanFokusChange = useCallback((isActive, focusedTaskIds) => {
    kanbanFokusChangeRef.current?.(isActive, focusedTaskIds);
  }, []);
  const handleTableFokusChange = useCallback((isActive, focusedTaskIds) => {
    tableFokusChangeRef.current?.(isActive, focusedTaskIds);
  }, []);
  const handleGanttFokusChange = useCallback((isActive, focusedTaskIds) => {
    ganttFokusChangeRef.current?.(isActive, focusedTaskIds);
  }, []);
  const handleTodoFokusChange = useCallback((isActive, focusedTaskIds) => {
    todoFokusChangeRef.current?.(isActive, focusedTaskIds);
  }, []);
  const handleNeuralFokusChange = useCallback((isActive, focusedTaskIds) => {
    neuralFokusChangeRef.current?.(isActive, focusedTaskIds);
  }, []);
  
  // Create FOKUS mode instances for each view
  const kanbanFokus = useFokusMode({ 
    tasks, 
    statuses, 
    nestedSprints, 
    selectedTaskIds: kanbanSelectedIds, 
    onFokusChange: handleKanbanFokusChange 
  });
  const tableFokus = useFokusMode({ 
    tasks, 
    statuses, 
    nestedSprints, 
    selectedTaskIds: tableSelectedIds, 
    onFokusChange: handleTableFokusChange 
  });
  const ganttFokus = useFokusMode({ 
    tasks, 
    statuses, 
    nestedSprints, 
    selectedTaskIds: ganttSelectedIds, 
    onFokusChange: handleGanttFokusChange 
  });
  const todoFokus = useFokusMode({ 
    tasks, 
    statuses, 
    nestedSprints, 
    selectedTaskIds: todoSelectedIds, 
    onFokusChange: handleTodoFokusChange 
  });
  const neuralFokus = useFokusMode({
    tasks,
    statuses,
    nestedSprints,
    selectedTaskIds: neuralSelectedIds,
    onFokusChange: handleNeuralFokusChange,
  });

  // Function to connect refs to custom view settings handlers
  const connectToCustomViewSettings = useCallback((customViewSettings) => {
    kanbanFokusChangeRef.current = customViewSettings.handleKanbanFokusChange;
    tableFokusChangeRef.current = customViewSettings.handleTableFokusChange;
    ganttFokusChangeRef.current = customViewSettings.handleGanttFokusChange;
    todoFokusChangeRef.current = customViewSettings.handleTodoFokusChange;
    neuralFokusChangeRef.current = customViewSettings.handleNeuralFokusChange;
  }, []);

  return {
    // FOKUS mode instances
    kanbanFokus,
    tableFokus,
    ganttFokus,
    todoFokus,
    neuralFokus,
    // Connection function
    connectToCustomViewSettings,
  };
};

/**
 * Hook to connect FOKUS refs to custom view settings
 * Call this after both hooks are created
 */
export const useFokusRefsConnection = (fokusModeRefs, customViewSettings) => {
  useEffect(() => {
    fokusModeRefs.connectToCustomViewSettings(customViewSettings);
  }, [
    fokusModeRefs.connectToCustomViewSettings,
    customViewSettings.handleKanbanFokusChange,
    customViewSettings.handleTableFokusChange,
    customViewSettings.handleGanttFokusChange,
    customViewSettings.handleTodoFokusChange,
    customViewSettings.handleNeuralFokusChange,
  ]);
};

export default useFokusModeRefs;
